import type {
  CustomerApplicationFieldValueContract,
  CustomerApplicationSourceLinkContract,
  CustomerFieldBindingContract,
  CustomerFieldMappingTarget,
} from '@/contracts/customer-application-binding'
import {
  type CustomerApplicationContract,
  type CustomerFieldMappingContract,
  type DuplicateResolutionMode,
  createDuplicateSignals,
} from '@/contracts/customers'
import type { BusinessViewMode } from '@/contracts/permissions'
import {
  type ApplicationStatus,
  type CustomerApplication,
  type ManagedCustomer,
  type SalesUser,
  type SalesUserImportSummary,
  applicationToCustomer,
  applyAppendOnlySalesUserImport,
  isReviewableApplicationStatus,
  normalizeEmail,
  parseSalesUsersCsv,
} from '@/lib/business-workflow'
import {
  buildManualFieldValues,
  createFieldBindingsFromMappings,
} from '@/lib/customer-application-binding'
import {
  createCustomerApplicationFromSourceRecord,
  createManualCustomerApplication,
  reviewCustomerApplication,
  saveCustomerToApi,
  syncCustomerApplicationToApi,
  syncCustomerFieldMappingsToApi,
  updateCustomerApplicationFieldValue,
} from '@/lib/data-client/customers'
import { isApiDataClientMode } from '@/lib/data-client/mode'
import {
  type BusinessWorkflowSnapshot,
  createDefaultBusinessWorkflowSnapshot,
  loadFixture,
  saveBusinessWorkflowSnapshot,
} from '@/lib/mock-api/persistent-store'
import type { SessionUser } from '@/types/api'
import { create } from 'zustand'

export type NewApplicationInput = Pick<
  CustomerApplication,
  | 'formId'
  | 'formTitle'
  | 'formDescription'
  | 'companyName'
  | 'taxId'
  | 'industry'
  | 'customerType'
  | 'departmentName'
  | 'companySize'
  | 'website'
  | 'companyAddress'
  | 'companyPhone'
  | 'contactName'
  | 'contactTitle'
  | 'contactEmail'
  | 'contactPhone'
  | 'decisionRole'
  | 'paymentTerms'
  | 'creditLimit'
  | 'salesRegion'
  | 'expectedValue'
  | 'source'
  | 'needSummary'
  | 'internalNotes'
>

export type SubmitApplicationOptions = {
  fieldValues?: CustomerApplicationFieldValueContract[]
  answers?: CustomerApplication['answers']
}

type BusinessWorkflowStore = {
  viewMode: Exclude<BusinessViewMode, 'readonly'>
  salesUsers: SalesUser[]
  applications: CustomerApplication[]
  customers: ManagedCustomer[]
  fieldMappings: CustomerFieldMappingContract[]
  fieldBindings: CustomerFieldBindingContract[]
  sourceLinks: CustomerApplicationSourceLinkContract[]
  fieldValues: Record<string, CustomerApplicationFieldValueContract[]>
  duplicateDecisions: Record<string, DuplicateResolutionMode>
  hydrateWorkflow: (snapshot: BusinessWorkflowSnapshot) => void
  setViewMode: (viewMode: Exclude<BusinessViewMode, 'readonly'>) => void
  upsertSalesUser: (user: SalesUser) => void
  importSalesUsersCsv: (csv: string, supervisorCsv?: string) => SalesUserImportSummary
  submitApplication: (
    input: NewApplicationInput,
    currentUser: SessionUser | null,
    options?: SubmitApplicationOptions,
  ) => void
  createApplicationFromSourceRecord: (
    sourceRecordId: string,
    currentUser: SessionUser | null,
    overrides?: Partial<Record<CustomerFieldMappingTarget, string | number | undefined>>,
  ) => Promise<CustomerApplicationContract>
  createManualApplication: (
    input: NewApplicationInput,
    currentUser: SessionUser | null,
  ) => Promise<CustomerApplicationContract>
  updateApplicationFieldValue: (
    applicationId: string,
    fieldKey: CustomerFieldMappingTarget,
    nextValue: string,
  ) => Promise<CustomerApplicationFieldValueContract[]>
  updateApplicationStatus: (
    applicationId: string,
    status: ApplicationStatus,
    reviewer: Pick<SessionUser, 'email' | 'name'> | null,
    reviewNotes: string,
  ) => Promise<void>
  updateCustomer: (customerId: string, patch: Partial<ManagedCustomer>) => Promise<ManagedCustomer>
  updateFieldMappings: (formId: string, mappings: CustomerFieldMappingContract[]) => void
  setDuplicateDecision: (applicationId: string, decision: DuplicateResolutionMode) => void
  resetWorkflow: () => void
}

function createId(prefix: string) {
  return `${prefix}-${crypto.randomUUID()}`
}

function resolveSubmitter(currentUser: SessionUser | null, salesUsers: SalesUser[]) {
  const matched = salesUsers.find(
    (salesUser) => normalizeEmail(salesUser.email) === normalizeEmail(currentUser?.email),
  )
  if (matched) return matched
  return salesUsers[0]
}

type WorkflowFields = Pick<
  BusinessWorkflowStore,
  | 'viewMode'
  | 'salesUsers'
  | 'applications'
  | 'customers'
  | 'fieldMappings'
  | 'fieldBindings'
  | 'sourceLinks'
  | 'fieldValues'
  | 'duplicateDecisions'
>

function toWorkflowSnapshot(state: WorkflowFields): BusinessWorkflowSnapshot {
  return {
    viewMode: state.viewMode,
    salesUsers: state.salesUsers,
    applications: state.applications,
    customers: state.customers,
    fieldMappings: state.fieldMappings,
    fieldBindings: state.fieldBindings,
    sourceLinks: state.sourceLinks,
    fieldValues: state.fieldValues,
    duplicateDecisions: state.duplicateDecisions,
  }
}

function persistWorkflowPatch(state: WorkflowFields, patch: Partial<BusinessWorkflowSnapshot>) {
  const next = { ...toWorkflowSnapshot(state), ...patch }
  saveBusinessWorkflowSnapshot(next)
  return patch
}

function upsertById<T extends { id: string }>(items: T[], nextItem: T) {
  const exists = items.some((item) => item.id === nextItem.id)
  return exists
    ? items.map((item) => (item.id === nextItem.id ? nextItem : item))
    : [nextItem, ...items]
}

function sourceLinkFromApplicationContract(
  application: CustomerApplicationContract,
): CustomerApplicationSourceLinkContract | null {
  const sourceSummary = application.sourceSummary
  if (
    sourceSummary?.sourceType !== 'questionnaire_record' ||
    !sourceSummary.sourceRecordId ||
    !sourceSummary.responseId ||
    !sourceSummary.formId
  ) {
    return null
  }

  return {
    id: `api-source-link-${application.id}`,
    workspaceId: 'api-shared-workspace',
    applicationId: application.id,
    sourceRecordId: sourceSummary.sourceRecordId,
    responseId: sourceSummary.responseId,
    formId: sourceSummary.formId,
    sourceType: 'questionnaire_record',
    status: 'active',
    createdAt: sourceSummary.submittedAt ?? application.submittedAt,
    createdByEmail: application.submittedByEmail,
    sourceMeta: application.sourceMeta,
  }
}

function upsertSourceLink(
  links: CustomerApplicationSourceLinkContract[],
  nextLink: CustomerApplicationSourceLinkContract,
) {
  const exists = links.some(
    (link) =>
      link.applicationId === nextLink.applicationId &&
      link.sourceRecordId === nextLink.sourceRecordId &&
      link.status === nextLink.status,
  )
  if (!exists) return [nextLink, ...links]
  return links.map((link) =>
    link.applicationId === nextLink.applicationId &&
    link.sourceRecordId === nextLink.sourceRecordId &&
    link.status === nextLink.status
      ? nextLink
      : link,
  )
}

function applicationContractPatch(
  state: WorkflowFields,
  application: CustomerApplicationContract,
): Partial<BusinessWorkflowSnapshot> {
  const patch: Partial<BusinessWorkflowSnapshot> = {
    applications: upsertById(state.applications, application),
  }

  if (application.fieldValues) {
    patch.fieldValues = {
      ...state.fieldValues,
      [application.id]: application.fieldValues,
    }
  }

  const sourceLink = sourceLinkFromApplicationContract(application)
  if (sourceLink) patch.sourceLinks = upsertSourceLink(state.sourceLinks, sourceLink)

  return patch
}

function customerPatchWithFiledApplicationSync(
  state: WorkflowFields,
  customer: ManagedCustomer,
): Partial<BusinessWorkflowSnapshot> {
  const patch: Partial<BusinessWorkflowSnapshot> = {
    customers: upsertById(state.customers, customer),
  }
  if (customer.filingStatus !== 'filed' || !customer.sourceApplicationId) return patch

  patch.applications = state.applications.map((application) =>
    application.id === customer.sourceApplicationId &&
    application.status === 'approved_pending_erp_filing'
      ? { ...application, status: 'customer_created' }
      : application,
  )
  return patch
}

const initialWorkflow = isApiDataClientMode()
  ? createDefaultBusinessWorkflowSnapshot()
  : loadFixture().businessWorkflow

export const useBusinessWorkflowStore = create<BusinessWorkflowStore>((set, get) => ({
  viewMode: initialWorkflow.viewMode,
  salesUsers: initialWorkflow.salesUsers,
  applications: initialWorkflow.applications,
  customers: initialWorkflow.customers,
  fieldMappings: initialWorkflow.fieldMappings,
  fieldBindings: initialWorkflow.fieldBindings,
  sourceLinks: initialWorkflow.sourceLinks,
  fieldValues: initialWorkflow.fieldValues,
  duplicateDecisions: initialWorkflow.duplicateDecisions,

  hydrateWorkflow: (snapshot) => {
    saveBusinessWorkflowSnapshot(snapshot)
    set(snapshot)
  },

  setViewMode: (viewMode) => set((state) => persistWorkflowPatch(state, { viewMode })),

  upsertSalesUser: (user) =>
    set((state) => {
      const exists = state.salesUsers.some((salesUser) => salesUser.id === user.id)
      const salesUsers = exists
        ? state.salesUsers.map((salesUser) => (salesUser.id === user.id ? user : salesUser))
        : [user, ...state.salesUsers]
      return persistWorkflowPatch(state, { salesUsers })
    }),

  importSalesUsersCsv: (csv, supervisorCsv) => {
    const parsed = parseSalesUsersCsv(csv, { supervisorCsv })
    if (parsed.errors.length > 0) {
      return {
        imported: parsed.users.length,
        created: 0,
        skippedExisting: 0,
        errors: parsed.errors,
      }
    }
    const result = applyAppendOnlySalesUserImport(get().salesUsers, parsed.users)
    set((state) => persistWorkflowPatch(state, { salesUsers: result.salesUsers }))
    return result.summary
  },

  submitApplication: (input, currentUser, options) =>
    set((state) => {
      const submitter = resolveSubmitter(currentUser, state.salesUsers)
      const supervisor =
        state.salesUsers.find(
          (salesUser) =>
            normalizeEmail(salesUser.email) === normalizeEmail(submitter.supervisorEmail),
        ) ?? submitter

      const fieldValues = options?.fieldValues ?? buildManualFieldValues(input)
      const applicationBase = {
        ...input,
        id: createId('app'),
        submittedByEmail: submitter.email,
        submittedByName: submitter.name,
        supervisorEmail: supervisor.email,
        supervisorName: supervisor.name,
        teamId: submitter.teamId,
        teamName: submitter.teamName,
        submittedAt: new Date().toLocaleString('zh-TW', { hour12: false }),
        reviewedAt: '',
        reviewerEmail: '',
        reviewerName: '',
        reviewNotes: '',
        answers: options?.answers ?? [
          {
            id: createId('ans'),
            questionTitle: '公司名稱',
            questionType: 'SHORT_TEXT',
            answer: input.companyName,
            mappedField: 'companyName',
          },
          {
            id: createId('ans'),
            questionTitle: '主要聯絡人',
            questionType: 'SHORT_TEXT',
            answer: `${input.contactName} / ${input.contactTitle}`,
            mappedField: 'contactName',
          },
          {
            id: createId('ans'),
            questionTitle: '客戶需求摘要',
            questionType: 'LONG_TEXT',
            answer: input.needSummary,
            mappedField: 'needSummary',
          },
        ],
      }
      const duplicateSignals = createDuplicateSignals(applicationBase, state.customers)
      const application: CustomerApplication = {
        ...applicationBase,
        status: duplicateSignals.length > 0 ? 'duplicate_pending' : 'pending_review',
      }
      syncCustomerApplicationToApi(application, currentUser)
      return persistWorkflowPatch(state, {
        applications: [application, ...state.applications],
        fieldValues: {
          ...state.fieldValues,
          [application.id]: fieldValues,
        },
      })
    }),

  createApplicationFromSourceRecord: async (sourceRecordId, currentUser, overrides) => {
    const application = await createCustomerApplicationFromSourceRecord({
      sourceRecordId,
      currentUser,
      overrides,
    })
    set((state) => persistWorkflowPatch(state, applicationContractPatch(state, application)))
    return application
  },

  createManualApplication: async (input, currentUser) => {
    const application = await createManualCustomerApplication({
      application: input,
      currentUser,
    })
    set((state) => persistWorkflowPatch(state, applicationContractPatch(state, application)))
    return application
  },

  updateApplicationFieldValue: async (applicationId, fieldKey, nextValue) => {
    const values = await updateCustomerApplicationFieldValue(applicationId, fieldKey, nextValue)
    set((state) =>
      persistWorkflowPatch(state, {
        fieldValues: {
          ...state.fieldValues,
          [applicationId]: values,
        },
      }),
    )
    return values
  },

  updateApplicationStatus: async (applicationId, status, reviewer, reviewNotes) => {
    if (isApiDataClientMode()) {
      const currentApplication = get().applications.find((item) => item.id === applicationId)
      if (!currentApplication || !isReviewableApplicationStatus(currentApplication.status)) return

      const reviewedApplication = await reviewCustomerApplication(
        applicationId,
        status,
        reviewNotes,
        get().duplicateDecisions[applicationId],
      )
      set((state) =>
        persistWorkflowPatch(state, applicationContractPatch(state, reviewedApplication)),
      )
      return
    }

    set((state) => {
      const application = state.applications.find((item) => item.id === applicationId)
      if (!application) return state
      if (!isReviewableApplicationStatus(application.status)) return state

      const reviewerName = reviewer?.name ?? '內部業務主管'
      const reviewerEmail = reviewer?.email ?? 'sales1-manager@miao-form.case4.app'
      const reviewedAt = new Date().toLocaleString('zh-TW', {
        hour12: false,
      })
      const persistedStatus: ApplicationStatus =
        status === 'approved' ? 'approved_pending_erp_filing' : status
      const nextApplications = state.applications.map((item) =>
        item.id === applicationId
          ? {
              ...item,
              status: persistedStatus,
              reviewedAt,
              reviewerEmail,
              reviewerName,
              reviewNotes,
            }
          : item,
      )

      if (status !== 'approved')
        return persistWorkflowPatch(state, {
          applications: nextApplications,
        })

      const approvedApplication = nextApplications.find((item) => item.id === applicationId)
      if (!approvedApplication)
        return persistWorkflowPatch(state, {
          applications: nextApplications,
        })

      const duplicateDecision = state.duplicateDecisions[applicationId]
      void duplicateDecision
      const nextCustomer = applicationToCustomer(
        { ...approvedApplication, reviewNotes },
        { reviewerEmail, reviewerName },
      )
      const exists = state.customers.some((customer) => customer.id === nextCustomer.id)
      return persistWorkflowPatch(state, {
        applications: nextApplications,
        customers: exists
          ? state.customers.map((customer) =>
              customer.id === nextCustomer.id ? nextCustomer : customer,
            )
          : [nextCustomer, ...state.customers],
      })
    })
  },

  updateCustomer: async (customerId, patch) => {
    if (isApiDataClientMode()) {
      const savedCustomer = await saveCustomerToApi(customerId, patch)
      set((state) =>
        persistWorkflowPatch(state, customerPatchWithFiledApplicationSync(state, savedCustomer)),
      )
      return savedCustomer
    }

    const current = get().customers.find((customer) => customer.id === customerId)
    if (!current) throw new Error('找不到客戶資料，無法儲存。')
    const updatedCustomer = {
      ...current,
      ...patch,
      updatedAt: new Date().toISOString().slice(0, 10),
    }
    set((state) =>
      persistWorkflowPatch(state, customerPatchWithFiledApplicationSync(state, updatedCustomer)),
    )
    return updatedCustomer
  },

  updateFieldMappings: (formId, mappings) =>
    set((state) => {
      syncCustomerFieldMappingsToApi(formId, mappings)
      const fieldMappings = [
        ...state.fieldMappings.filter((mapping) => mapping.formId !== formId),
        ...mappings,
      ]
      return persistWorkflowPatch(state, {
        fieldMappings,
        fieldBindings: [
          ...state.fieldBindings.filter((binding) => binding.formId !== formId),
          ...createFieldBindingsFromMappings(mappings),
        ],
      })
    }),

  setDuplicateDecision: (applicationId, decision) =>
    set((state) =>
      persistWorkflowPatch(state, {
        duplicateDecisions: {
          ...state.duplicateDecisions,
          [applicationId]: decision,
        },
        applications:
          decision === 'new_customer'
            ? state.applications.map((application) =>
                application.id === applicationId
                  ? { ...application, status: 'pending_review' }
                  : application,
              )
            : state.applications,
      }),
    ),
  resetWorkflow: () => {
    const workflow = createDefaultBusinessWorkflowSnapshot()
    saveBusinessWorkflowSnapshot(workflow)
    set(workflow)
  },
}))
